import React from 'react';
import PageTemplate from '../../components/Common/PageTemplate';
import { ExternalLink, FileText, Scale, GraduationCap, Users, BookOpen, Shield, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Laws = () => {
  const { t } = useTranslation();
  const lawsAndRegulations = t('lawsPage.lawsAndRegulations', { returnObjects: true });
  const studentDocuments = t('lawsPage.studentDocuments', { returnObjects: true });

  const iconMapping = {
    Scale: <Scale className="w-6 h-6" />,
    FileText: <FileText className="w-5 h-5" />,
    GraduationCap: <GraduationCap className="w-5 h-5" />,
    BookOpen: <BookOpen className="w-5 h-5" />,
    Users: <Users className="w-5 h-5" />,
    ExternalLink: <ExternalLink className="w-5 h-5" />,
    Shield: <Shield className="w-5 h-5" />
  };

  return (
    <PageTemplate
      title={t('lawsPage.title')}
      subtitle={t('lawsPage.subtitle')}
    >
      <div className="max-w-7xl mx-auto">
        {/* Hero Section - Kompakt */}
        <div className="relative bg-gradient-to-r from-[#002D72] via-blue-700 to-[#002D72] rounded-2xl shadow-2xl p-6 mb-8 overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-24 translate-x-24"></div>
          <div className="absolute bottom-0 left-0 w-36 h-36 bg-white/5 rounded-full translate-y-18 -translate-x-18"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-sm">
                <Scale className="w-10 h-10 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white text-center mb-3">
              {t('lawsPage.heroTitle')}
            </h2>
            <p className="text-lg text-blue-100 text-center max-w-3xl mx-auto">
              {t('lawsPage.heroDescription')}
          </p>
          </div>
        </div>

        {/* Kanun ve Yönetmelikler Kartları - Kompakt */}
        <div className="grid gap-6 mb-8">
          {lawsAndRegulations && lawsAndRegulations.map((category, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300">
              <div className={`${category.bgColor} p-4`}>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                    {iconMapping[category.icon]}
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    {category.title}
              </h3>
                </div>
              </div>
              <div className="p-4">
                <div className="grid gap-3">
                {category.items.map((item, itemIndex) => (
                    <a
                      key={itemIndex}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group flex items-center justify-between p-3 rounded-xl border-2 ${category.borderColor} hover:bg-blue-50 transition-all duration-300 hover:shadow-md`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 bg-blue-100 rounded-lg ${category.textColor} group-hover:bg-blue-200 transition-colors`}>
                          <FileText className="w-4 h-4" />
                        </div>
                        <span className="font-semibold text-gray-800 group-hover:text-blue-700 transition-colors">
                        {item.title}
                        </span>
                      </div>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    </a>
                  ))}
                  </div>
              </div>
            </div>
          ))}
        </div>

        {/* Öğrenciler İçin Bölümü - Grid Layout */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{t('lawsPage.studentsTitle')}</h3>
                <p className="text-green-100 text-sm">{t('lawsPage.studentsSubtitle')}</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {studentDocuments && studentDocuments.map((doc, index) => (
                <a
                  key={index}
                  href={doc.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start space-x-3 p-3 rounded-xl border-2 border-green-200 hover:bg-green-50 transition-all duration-300 hover:shadow-md"
                >
                  <div className="flex-shrink-0 p-2 bg-green-100 rounded-lg text-green-600 group-hover:bg-green-200 transition-colors">
                    {iconMapping[doc.icon]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-800 group-hover:text-green-700 transition-colors text-sm leading-tight">
                      {doc.title}
                    </h4>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Uyarı ve Bilgilendirme - Kompakt */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-r-xl shadow-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-amber-800 mb-1">{t('lawsPage.importantInfoTitle')}</h4>
              <p className="text-amber-700 text-sm leading-relaxed">
                {t('lawsPage.importantInfoText')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageTemplate>
  );
};

export default Laws;